import { NgZone, ElementRef } from '@angular/core';
import ResizeObserver from 'resize-observer-polyfill';
import { throttle } from 'underscore';


import {
	Directive,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';

/**
 * vbWatchDimensions directive
 * Watches the dimensions of the container it is placed on and resizes the specified child content region for best fit while maintaining its proportions.
 *
 * OPTIONS
 *
 * vbWatchDimensions
 * Value should be an array of child elements (raw element or jQuery selection) that you wish to resize so that it maintains its proportion
 * when this element's size has changed. Leveraging teamplate variable (#) is suggested to gain references to elements. if the element is an
 * angular component then use @ViewChild read: ElementRef option to get the html reference.
 *
 * natural-ratio
 * By default this assumes the content is 16:9 aspect. To override, set the natural-ratio boolean input flag to true.
 * and this will calculate the aspect ratio at first run based on the natural dimensions of the provided element to resize.
 *
 */
@Directive({
	selector: '[vbWatchDimensions]',
	standalone: false
})
export class VbWatchDimensionsDirective implements OnInit, OnChanges, OnDestroy {
	@Input() public vbWatchDimensions: Array<HTMLElement | ElementRef<HTMLElement>>;
	@Input() public naturalRatio: boolean;
	@Input() public padding: number = 32;
	@Input() public sidePadding: number = 0;
	@Input() public verticalPadding: number = 0;

	@Output() public dimensionsChanged: EventEmitter<any> = new EventEmitter();

	private readonly throttledEmit = throttle(e =>
		this.ngZone.run(() => {
			this.dimensionsChanged.emit(e);
		}), 50);

	private observer: ResizeObserver;
	private lastEntry: ResizeObserverEntry;

	public elementsToResize: HTMLElement[];
	private animationFrame: number;
	private aspect: number;

	constructor(
		private readonly el: ElementRef,
		private readonly ngZone: NgZone
	) {
	}

	public ngOnInit(): void {
		this.updateAspectRatio();

		this.observer = new ResizeObserver(entries => {
			this.lastEntry = entries[0];
			this.scheduleRedraw();
		});

		//redraw on resize
		this.ngZone.runOutsideAngular(() => {
			// performs very poorly with zone.js
			this.observer.observe(this.el.nativeElement);
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.vbWatchDimensions) {
			this.elementsToResize = Array.isArray(this.vbWatchDimensions)
				? this.vbWatchDimensions.map(el => (el as ElementRef<HTMLElement>).nativeElement || el as HTMLElement)
				: [];
			this.updateAspectRatio();
			this.scheduleRedraw();
		}
	}

	public ngOnDestroy(): void {
		this.observer.disconnect();
		if (this.animationFrame) {
			cancelAnimationFrame(this.animationFrame);
		}
	}

	private scheduleRedraw(force?: boolean): void {
		if (this.animationFrame && !force) {
			return;
		}
		this.animationFrame = requestAnimationFrame(() => {
			this.animationFrame = null;
			this.redraw();
		});
	}

	private updateAspectRatio(): void {
		if (!this.naturalRatio || !this.elementsToResize?.length) {
			this.aspect = 16 / 9;
			return;
		}
		//extract the ratio from the natural dimensions of the element
		const image: HTMLImageElement = this.elementsToResize[0] as HTMLImageElement;

		image.onload = () => {
			this.aspect = image.naturalWidth / image.naturalHeight;
			this.scheduleRedraw(true);
		};
	}

	private redraw(): void {
		const rect = this.lastEntry?.contentRect
			?? this.el.nativeElement.getBoundingClientRect();

		let { width, height } = rect;

		if (!this.aspect || !width || !height) {
			return;
		}

		width -= this.padding + this.sidePadding;
		height -= this.padding + this.verticalPadding;

		const [w, h] = (width / height < this.aspect) ?
			this.resizeByWidth(width) :
			this.resizeByHeight(height);

		const widthStyle = `${w}px`;
		const heightStyle = this.naturalRatio ? '' : `${h}px`;

		for (const el of this.elementsToResize) {
			el.style.width = widthStyle;
			el.style.height = heightStyle;
		}

		this.throttledEmit({ height: h, width: w });
	}

	private resizeByHeight(height: number): [number, number] {
		const h = height;
		const w = h * this.aspect;
		return [w, h];
	}

	private resizeByWidth(width: number): [number, number] {
		const w = width;
		const h = w / this.aspect;
		return [w, h];
	}

}
